import React, { useState, useEffect, useMemo, useRef } from "react";
import { facultyMembersApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./FacultyMembers.scss";

// ─── Constants ────────────────────────────────────────────────────────────────
const DESIGNATIONS = [
  'Professor', 'Associate Professor', 'Assistant Professor', 
  'HOD', 'Dean', 'Lecturer', 'Lab Instructor'
];

const QUALIFICATIONS = [
  'Ph.D.', 'M.Phil.', 'M.A.', 'M.Sc.', 'M.Com.', 'M.B.A.', 
  'M.C.A.', 'M.Tech.', 'B.Ed.', 'Other'
];

const COURSES = [
  'B.A.', 'B.Sc.', 'B.Com.', 'B.B.A.', 'B.C.A.', 'B.Tech.', 
  'M.A.', 'M.Sc.', 'M.Com.', 'M.B.A.', 'M.C.A.', 'M.Tech.', 'Ph.D.'
];

const SEMESTERS = ['Sem 1', 'Sem 2', 'Sem 3', 'Sem 4', 'Sem 5', 'Sem 6', 'Sem 7', 'Sem 8'];

const STATUS_META = {
  Active:    { label: "Active",    color: "#22c55e", bg: "#f0fdf4", border: "#bbf7d0" },
  "On Leave": { label: "On Leave", color: "#f59e0b", bg: "#fffbeb", border: "#fde68a" },
  Inactive:  { label: "Inactive",  color: "#ef4444", bg: "#fef2f2", border: "#fecaca" },
};

const COURSE_COLORS = [
  ["#7c3aed", "#f5f3ff"], ["#0ea5e9", "#f0f9ff"], ["#10b981", "#ecfdf5"],
  ["#f59e0b", "#fffbeb"], ["#ec4899", "#fdf2f8"], ["#6366f1", "#eef2ff"],
  ["#14b8a6", "#f0fdfa"], ["#f97316", "#fff7ed"], ["#8b5cf6", "#f5f3ff"],
  ["#0891b2", "#ecfeff"], ["#d97706", "#fffbeb"], ["#059669", "#ecfdf5"],
  ["#dc2626", "#fef2f2"],
];

const getCourseColor = (course) => COURSE_COLORS[COURSES.indexOf(course) % COURSE_COLORS.length] || COURSE_COLORS[0];

const defaultForm = {
  _id: null,
  Full_Name: "",
  Employee_ID: "",
  Designation: "Assistant Professor",
  Qualification: "M.Sc.",
  Email: "",
  Phone: "",
  Gender: "Male",
  Joining_Date: "",
  Course: "B.Sc.",
  Semesters: [],
  Specialization: "",
  Cabin_Room_No: "",
  Subjects_Teaching: [],
  Experience: "",
  Status: "Active",
  About_Bio: "",
};

const FacultyMembers = () => {
  const [members, setMembers]               = useState([]);
  const [loading, setLoading]               = useState(false);
  const [modalType, setModalType]           = useState("");
  const [formData, setFormData]             = useState(defaultForm);
  const [selectedItem, setSelectedItem]     = useState(null);
  const [confirmId, setConfirmId]           = useState(null);
  const [confirmAction, setConfirmAction]   = useState("");
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownPos, setDropdownPos]       = useState({ top: 0, left: 0 });
  const [filterCourse, setFilterCourse]     = useState("all");
  const [filterStatus, setFilterStatus]     = useState("all");
  const [searchText, setSearchText]         = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await apiClient(facultyMembersApi);
      const result = await response.json();
      if (response.ok) {
        setMembers(result.data || []);
      }
    } catch (error) {
      console.error("Error fetching faculty members:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onOut   = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpenId(null); };
    const onClose = () => setDropdownOpenId(null);
    document.addEventListener("mousedown", onOut);
    window.addEventListener("scroll", onClose, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", onOut);
      window.removeEventListener("scroll", onClose, true);
      window.removeEventListener("resize", onClose);
    };
  }, []);

  // ── Derived ────────────────────────────────────────────────────────────────
  const filtered = useMemo(() => members.filter((m) => {
    const q = searchText.toLowerCase();
    const matchSearch =
      (m.Full_Name || "").toLowerCase().includes(q) ||
      (m.Employee_ID || "").toLowerCase().includes(q) ||
      (m.Email || "").toLowerCase().includes(q) ||
      (m.Specialization || "").toLowerCase().includes(q);
    return matchSearch &&
      (filterCourse === "all" || m.Course === filterCourse) &&
      (filterStatus === "all" || m.Status === filterStatus);
  }), [members, searchText, filterCourse, filterStatus]);

  const activeCount   = members.filter((m) => m.Status === "Active").length;
  const onLeaveCount  = members.filter((m) => m.Status === "On Leave").length;
  const inactiveCount = members.filter((m) => m.Status === "Inactive").length;
  const hasFilters    = filterCourse !== "all" || filterStatus !== "all" || !!searchText;

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleDotsClick = (e, id) => {
    e.stopPropagation();
    if (dropdownOpenId === id) { setDropdownOpenId(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const mW = 200, mH = 220, gap = 8;
    let left = rect.right - mW;
    let top  = rect.bottom + gap;
    if (left < 12) left = 12;
    if (left + mW > window.innerWidth - 12) left = window.innerWidth - mW - 12;
    if (top  + mH > window.innerHeight - 12) top = rect.top - mH - gap;
    if (top  < 12) top = 12;
    setDropdownPos({ top, left });
    setDropdownOpenId(id);
  };

  const openAddModal = () => {
    setFormData(defaultForm);
    setModalType("add");
    setDropdownOpenId(null);
  };
  const openViewModal = (m) => { setSelectedItem(m); setModalType("view"); setDropdownOpenId(null); };
  const openEditModal = (m) => {
    setFormData({ ...m, Joining_Date: m.Joining_Date ? m.Joining_Date.split('T')[0] : "" });
    setModalType("edit");
    setDropdownOpenId(null);
  };
  const closeModal = () => { setModalType(""); setSelectedItem(null); setFormData(defaultForm); };
  const openConfirm = (id, action) => { setDropdownOpenId(null); setConfirmId(id); setConfirmAction(action); };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const toggleSemester = (sem) => {
    setFormData(prev => ({
      ...prev,
      Semesters: prev.Semesters.includes(sem)
        ? prev.Semesters.filter(s => s !== sem)
        : [...prev.Semesters, sem]
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = modalType === "add" ? "POST" : "PUT";
      const url = modalType === "add" ? facultyMembersApi : `${facultyMembersApi}/${formData._id}`;
      const response = await apiClient(url, {
        method,
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        fetchMembers();
        closeModal();
      }
    } catch (error) {
      console.error("Error saving faculty member:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      setLoading(true);
      const item = members.find(m => m._id === id);
      const response = await apiClient(`${facultyMembersApi}/${id}`, {
        method: "PUT",
        body: JSON.stringify({ ...item, Status: newStatus })
      });
      if (response.ok) {
        fetchMembers();
        setConfirmId(null); setConfirmAction("");
      }
    } catch (error) {
      console.error("Error updating status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const response = await apiClient(`${facultyMembersApi}/${id}`, { method: "DELETE" });
      if (response.ok) {
        fetchMembers();
        setConfirmId(null); setConfirmAction("");
      }
    } catch (error) {
      console.error("Error deleting faculty member:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearAllFilters = () => { setFilterCourse("all"); setFilterStatus("all"); setSearchText(""); };

  return (
    <div className="fm-page">
      {/* ── Header ── */}
      <div className="fm-header">
        <div className="fm-header-left">
          <div className="fm-header-icon">👨‍🏫</div>
          <div>
            <div className="fm-breadcrumb">
              <span>Department at a Glance</span>
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
              <span className="fm-bc-active">Faculty Members</span>
            </div>
            <h1>Faculty Members</h1>
            <p>Manage faculty details, designations, qualifications, and teaching assignments for the current academic session.</p>
          </div>
        </div>
        <button type="button" className="fm-add-btn" onClick={openAddModal}>
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          Add Faculty Member
        </button>
      </div>

      {/* ── Stats ── */}
      <div className="fm-stats">
        {[
          { cls: "total",    icon: "👥", label: "Total Faculty", val: members.length, key: "all" },
          { cls: "active",   icon: "✅", label: "Active",        val: activeCount,   key: "Active" },
          { cls: "onleave",  icon: "⏳", label: "On Leave",      val: onLeaveCount,  key: "On Leave" },
          { cls: "inactive", icon: "🚫", label: "Inactive",      val: inactiveCount, key: "Inactive" },
        ].map((s) => (
          <div key={s.cls}
            className={`fm-stat-card ${s.cls} ${filterStatus === s.key ? "active-filter" : ""}`}
            onClick={() => setFilterStatus(s.key)}>
            <div className="stat-icon-box">{s.icon}</div>
            <div className="stat-content">
              <span>{s.label}</span>
              <strong>{s.val}</strong>
            </div>
          </div>
        ))}
      </div>

      {/* ── Toolbar ── */}
      <div className="fm-toolbar">
        <div className="toolbar-search">
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
          <input type="text" placeholder="Search by name, ID, email or specialization..."
            value={searchText} onChange={(e) => setSearchText(e.target.value)} />
          {searchText && <button type="button" className="clear-search" onClick={() => setSearchText("")}>×</button>}
        </div>

        <div className="toolbar-filters">
          <div className="toolbar-filter-group">
            <label>Course</label>
            <select value={filterCourse} onChange={(e) => setFilterCourse(e.target.value)}
              className={`toolbar-select ${filterCourse !== "all" ? "active-select" : ""}`}>
              <option value="all">All Courses</option>
              {COURSES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="toolbar-filter-group">
            <label>Status</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              className={`toolbar-select ${filterStatus !== "all" ? "active-select" : ""}`}>
              <option value="all">All Statuses</option>
              {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          {hasFilters && <button type="button" className="clear-filters-btn" onClick={clearAllFilters}><svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>Clear</button>}
          <span className="result-count"><strong>{filtered.length}</strong> of <strong>{members.length}</strong></span>
        </div>
      </div>

      {/* ── Filter Pills ── */}
      {hasFilters && (
        <div className="active-filter-pills">
          {searchText && <span className="filter-pill">"{searchText}" <button type="button" onClick={() => setSearchText("")}>×</button></span>}
          {filterCourse !== "all" && <span className="filter-pill">Course: {filterCourse} <button type="button" onClick={() => setFilterCourse("all")}>×</button></span>}
          {filterStatus !== "all" && <span className="filter-pill">Status: {STATUS_META[filterStatus]?.label} <button type="button" onClick={() => setFilterStatus("all")}>×</button></span>}
          <button type="button" className="pill-clear-all" onClick={clearAllFilters}>Clear all</button>
        </div>
      )}

      {/* ── Faculty Grid ── */}
      {loading ? (
        <div className="loading-overlay">Loading faculty members...</div>
      ) : filtered.length > 0 ? (
        <div className="fm-grid">
          {filtered.map((m) => {
            const sm = STATUS_META[m.Status] || STATUS_META.Active;
            const [ac, ab] = getCourseColor(m.Course);
            return (
              <div key={m._id} className="fm-card" onClick={() => openViewModal(m)}>
                <div className="fmc-top">
                  <div className="fmc-avatar" style={{ background: ab, color: ac }}>
                    {m.Full_Name[0]}
                    <div className="fmc-status-dot" style={{ background: sm.color }} />
                  </div>
                  <div className="fmc-top-meta">
                    <div className="fmc-name">{m.Full_Name}</div>
                    <div className="fmc-desig">{m.Designation}</div>
                    <div className="fmc-emp-id">{m.Employee_ID}</div>
                  </div>
                  <button type="button" className="dots-btn" onClick={(e) => handleDotsClick(e, m._id)}>⋮</button>
                </div>
                
                <div className="fmc-course-row">
                  <span className="fmc-course-badge" style={{ color: ac, background: ab }}>{m.Course}</span>
                  <span className="fmc-qual">{m.Qualification}</span>
                </div>

                {m.Specialization && <div className="fmc-spec">{m.Specialization}</div>}

                {m.Semesters && m.Semesters.length > 0 && (
                  <div className="fmc-sems">
                    {m.Semesters.slice(0, 3).map(s => <span key={s} className="sem-chip">{s}</span>)}
                    {m.Semesters.length > 3 && <span className="sem-chip more">+{m.Semesters.length - 3}</span>}
                  </div>
                )}

                <div className="fmc-footer">
                  <div className="fmc-footer-left">
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                    <span>{m.Email}</span>
                  </div>
                  <span className="fmc-status-badge" style={{ color: sm.color, background: sm.bg }}>{sm.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="fm-empty">
          <div className="empty-icon">👨‍🏫</div>
          <h3>No faculty members found</h3>
          <p>{hasFilters ? "Try adjusting your filters or search term." : "No records found. Click \"Add Faculty Member\" to begin."}</p>
          {hasFilters && <button type="button" className="fm-clear-btn" onClick={clearAllFilters}>Clear Filters</button>}
        </div>
      )}

      {/* ── Dropdown ── */}
      {dropdownOpenId !== null && (
        <div ref={dropdownRef} className="action-dropdown"
          style={{ position: "fixed", top: `${dropdownPos.top}px`, left: `${dropdownPos.left}px`, zIndex: 99999 }}>
          <button type="button" onClick={() => openViewModal(members.find(m => m._id === dropdownOpenId))}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" /><circle cx="12" cy="12" r="3" /></svg>
            View Details
          </button>
          <button type="button" onClick={() => openEditModal(members.find(m => m._id === dropdownOpenId))}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>
            Edit Member
          </button>
          <div className="dropdown-divider" />
          <button type="button" className="dd-delete" onClick={() => openConfirm(dropdownOpenId, "delete")}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" /></svg>
            Delete
          </button>
        </div>
      )}

      {/* ── Confirm Modal ── */}
      {confirmId !== null && (
        <div className="modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className={`confirm-icon ci-${confirmAction}`}>
              <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" /></svg>
            </div>
            <h3>Delete Faculty Member?</h3>
            <p>This action cannot be undone. All record of this member will be permanently removed.</p>
            <div className="confirm-actions">
              <button type="button" className="btn btn-light" onClick={() => setConfirmId(null)}>Cancel</button>
              <button type="button" className="btn btn-danger" onClick={() => handleDelete(confirmId)}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add / Edit Modal ── */}
      {(modalType === "add" || modalType === "edit") && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="fm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-left">
                <div className="modal-header-icon">👨‍🏫</div>
                <div>
                  <h2>{modalType === "add" ? "Add Faculty Member" : "Edit Faculty Member"}</h2>
                  <span>{formData.Full_Name ? `Managing: ${formData.Full_Name}` : "Enter professional details below"}</span>
                </div>
              </div>
              <button type="button" className="modal-close" onClick={closeModal}>×</button>
            </div>

            <form className="modal-form" onSubmit={handleSave}>
              <div className="tab-content">
                <div className="form-section-label">Personal & Basic Info</div>
                <div className="form-group full">
                  <label>Full Name <span className="req">*</span></label>
                  <input type="text" name="Full_Name" value={formData.Full_Name || ""} onChange={handleChange} placeholder="e.g. Dr. Satish Chandra" required />
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Employee ID</label>
                    <input type="text" name="Employee_ID" value={formData.Employee_ID || ""} onChange={handleChange} placeholder="e.g. FAC-CS-101" />
                  </div>
                  <div className="form-group">
                    <label>Gender</label>
                    <div className="gender-picker">
                      {['Male', 'Female', 'Other'].map(g => (
                        <button key={g} type="button" className={`gender-btn ${formData.Gender === g ? 'selected' : ''}`} onClick={() => handleChange({ target: { name: "Gender", value: g } })}>{g}</button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Designation <span className="req">*</span></label>
                    <select name="Designation" value={formData.Designation || ""} onChange={handleChange}>
                      {DESIGNATIONS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Qualification <span className="req">*</span></label>
                    <select name="Qualification" value={formData.Qualification || ""} onChange={handleChange}>
                      {QUALIFICATIONS.map(q => <option key={q} value={q}>{q}</option>)}
                    </select>
                  </div>
                </div>

                <div className="form-section-label">Academic Assignments</div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Primary Course</label>
                    <select name="Course" value={formData.Course || ""} onChange={handleChange}>
                      {COURSES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Joining Date</label>
                    <input type="date" name="Joining_Date" value={formData.Joining_Date || ""} onChange={handleChange} />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Specialization</label>
                    <input type="text" name="Specialization" value={formData.Specialization || ""} onChange={handleChange} placeholder="e.g. Machine Learning" />
                  </div>
                  <div className="form-group">
                    <label>Experience</label>
                    <input type="text" name="Experience" value={formData.Experience || ""} onChange={handleChange} placeholder="e.g. 10 Years" />
                  </div>
                </div>

                <div className="form-group full">
                  <label>Subjects Teaching <span className="field-hint-inline">(Comma separated)</span></label>
                  <input 
                    type="text" 
                    value={(formData.Subjects_Teaching || []).join(", ")} 
                    onChange={(e) => setFormData({ ...formData, Subjects_Teaching: e.target.value.split(",").map(s => s.trim()) })} 
                    placeholder="e.g. Data Structures, Algorithms, C++" 
                  />
                </div>

                <div className="form-group full">
                  <label>Assigned Semesters <span className="field-hint-inline">(Select multiple)</span></label>
                  <div className="sem-multi-grid">
                    {SEMESTERS.map(sem => (
                      <button key={sem} type="button" className={`sem-multi-btn ${formData.Semesters.includes(sem) ? 'selected' : ''}`} onClick={() => toggleSemester(sem)}>
                        {sem}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-section-label">Contact & Placement</div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Official Email <span className="req">*</span></label>
                    <input type="email" name="Email" value={formData.Email || ""} onChange={handleChange} placeholder="faculty@college.edu" required />
                  </div>
                  <div className="form-group">
                    <label>Phone Number</label>
                    <input type="text" name="Phone" value={formData.Phone || ""} onChange={handleChange} placeholder="+91..." />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Cabin / Room No</label>
                    <input type="text" name="Cabin_Room_No" value={formData.Cabin_Room_No || ""} onChange={handleChange} placeholder="e.g. 202-B" />
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <div className="status-pill-row">
                      {Object.entries(STATUS_META).map(([k, v]) => (
                        <button key={k} type="button" className={`status-pill-btn ${formData.Status === k ? 'selected' : ''}`} onClick={() => handleChange({ target: { name: "Status", value: k } })}>
                          <span className="spb-dot" style={{ background: v.color }} />
                          {v.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="form-group full">
                  <label>Brief Bio</label>
                  <textarea name="About_Bio" value={formData.About_Bio || ""} onChange={handleChange} placeholder="Short professional biography..." rows="3" />
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? "Saving..." : modalType === "add" ? "Add Faculty" : "Update Records"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── View Modal ── */}
      {modalType === "view" && selectedItem && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="fm-modal view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-left">
                <div className="view-avatar" style={{ background: getCourseColor(selectedItem.Course)[1], color: getCourseColor(selectedItem.Course)[0] }}>
                  {selectedItem.Full_Name[0]}
                  <div className="view-avatar-status" style={{ background: STATUS_META[selectedItem.Status]?.color || "#22c55e" }} />
                </div>
                <div>
                  <div className="view-name">{selectedItem.Full_Name}</div>
                  <div className="view-desig-row">
                    <span className="view-desig">{selectedItem.Designation}</span>
                    <span className="view-qual">{selectedItem.Qualification}</span>
                    <span className="view-code">{selectedItem.Employee_ID}</span>
                  </div>
                </div>
              </div>
              <div className="view-header-actions">
                <button type="button" className="btn btn-light" style={{ padding: '8px 12px' }} onClick={() => openEditModal(selectedItem)}>Edit</button>
                <button type="button" className="modal-close" onClick={closeModal} style={{ marginLeft: 10 }}>×</button>
              </div>
            </div>

            <div className="view-modal-body">
              <div className="view-info-grid">
                <div className="vig-item">
                  <div className="vig-icon">📧</div>
                  <div><span className="vig-label">Email</span><span className="vig-val">{selectedItem.Email}</span></div>
                </div>
                <div className="vig-item">
                  <div className="vig-icon">📞</div>
                  <div><span className="vig-label">Phone</span><span className="vig-val">{selectedItem.Phone || "—"}</span></div>
                </div>
                <div className="vig-item">
                  <div className="vig-icon">🏛</div>
                  <div><span className="vig-label">Course</span><span className="vig-val">{selectedItem.Course}</span></div>
                </div>
                <div className="vig-item">
                  <div className="vig-icon">🏢</div>
                  <div><span className="vig-label">Cabin</span><span className="vig-val">{selectedItem.Cabin_Room_No || "—"}</span></div>
                </div>
                <div className="vig-item">
                  <div className="vig-icon">🎓</div>
                  <div><span className="vig-label">Experience</span><span className="vig-val">{selectedItem.Experience || "—"}</span></div>
                </div>
                <div className="vig-item">
                  <div className="vig-icon">📅</div>
                  <div><span className="vig-label">Joined</span><span className="vig-val">{selectedItem.Joining_Date ? new Date(selectedItem.Joining_Date).toLocaleDateString() : "—"}</span></div>
                </div>
                {selectedItem.Specialization && (
                  <div className="vig-item" style={{ gridColumn: '1 / -1' }}>
                    <div className="vig-icon">🎯</div>
                    <div><span className="vig-label">Specialization</span><span className="vig-val">{selectedItem.Specialization}</span></div>
                  </div>
                )}
              </div>

              {selectedItem.Subjects_Teaching && selectedItem.Subjects_Teaching.length > 0 && (
                <div className="view-section">
                  <div className="vs-label">Subjects Taught</div>
                  <div className="view-subs">
                    {selectedItem.Subjects_Teaching.map(s => <span key={s} className="subject-tag">{s}</span>)}
                  </div>
                </div>
              )}

              {selectedItem.About_Bio && (
                <div className="view-section">
                  <div className="vs-label">Professional Bio</div>
                  <div className="view-about">{selectedItem.About_Bio}</div>
                </div>
              )}

              <div className="view-section">
                <div className="vs-label">Teaching Assignments</div>
                <div className="view-sems">
                  {(selectedItem.Semesters || []).map(s => <span key={s} className="sem-chip">{s}</span>)}
                </div>
              </div>
              
              <div className="view-footer-actions">
                <button type="button" className="btn btn-light" onClick={closeModal}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacultyMembers;